import { Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import { useAuth } from './context/AuthContext';
import { ConfirmProvider } from './context/ConfirmContext';
import { DropdownProvider } from './context/DropdownContext';
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import Layout from './components/Layout';
import {
  BewerberListPage,
  BewerberDetailPage,
  BewerberCreatePage,
  BewerberBearbeitenPage,
  VorstellungsgespraechPage,
} from './pages/bewerber';
import {
  BedarfeListPage,
  BedarfDetailPage,
  BedarfCreatePage,
  BedarfEditPage,
} from './pages/bedarfe';
import { StellenausschreibungenPage } from './pages/stellenausschreibungen';
import {
  EmailTemplatesPage,
  EmailTemplateEditPage,
  EmailLogsPage,
  IncomingEmailsPage,
} from './pages/email';
import {
  UsersListPage,
  UserCreatePage,
  UserEditPage,
} from './pages/users';
import { ProfileSettingsPage } from './pages/profile';
import { ForgotPasswordPage, ResetPasswordPage, AcceptInvitationPage } from './pages/auth';
import { AdminLogsPage, StadtListPage, InvitationsPage, SessionsPage, SettingsPage } from './pages/admin';

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

function App() {
  return (
    <DropdownProvider>
    <ConfirmProvider>
      <Toaster
        position="bottom-right"
        richColors
        closeButton
        toastOptions={{
          duration: 4000,
          className: 'font-sans',
        }}
      />
      <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
      <Route path="/accept-invitation/:token" element={<AcceptInvitationPage />} />
      {/* Full-screen routes (no sidebar) */}
      <Route
        path="/bewerber/:id/bearbeiten"
        element={
          <ProtectedRoute>
            <BewerberBearbeitenPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/bewerber/:id/vorstellungsgespraech"
        element={
          <ProtectedRoute>
            <VorstellungsgespraechPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/*"
        element={
          <ProtectedRoute>
            <Layout>
              <Routes>
                <Route path="/" element={<DashboardPage />} />
                <Route path="/profile" element={<ProfileSettingsPage />} />
                {/* Bewerber Routes */}
                <Route path="/bewerber" element={<BewerberListPage />} />
                <Route path="/bewerber/new" element={<BewerberCreatePage />} />
                <Route path="/bewerber/:id" element={<BewerberDetailPage />} />
                {/* Bedarfe Routes */}
                <Route path="/bedarfe" element={<BedarfeListPage />} />
                <Route path="/bedarfe/new" element={<BedarfCreatePage />} />
                <Route path="/bedarfe/:id" element={<BedarfDetailPage />} />
                <Route path="/bedarfe/:id/edit" element={<BedarfEditPage />} />

                {/* Stellenausschreibungen Routes */}
                <Route
                  path="/stellenausschreibungen"
                  element={<StellenausschreibungenPage />}
                />
                {/* Kennenlernen temporarily disabled ("Bald verfügbar") - redirect to dashboard */}
                <Route path="/kennenlernen" element={<Navigate to="/" replace />} />
                {/* Email Routes */}
                <Route path="/email/templates" element={<EmailTemplatesPage />} />
                <Route path="/email/templates/new" element={<EmailTemplateEditPage />} />
                <Route path="/email/templates/:id" element={<EmailTemplateEditPage />} />
                <Route path="/email/logs" element={<EmailLogsPage />} />
                <Route path="/email/incoming" element={<IncomingEmailsPage />} />
                <Route path="/users" element={<UsersListPage />} />
                <Route path="/users/new" element={<UserCreatePage />} />
                <Route path="/users/:id/edit" element={<UserEditPage />} />
                {/* Admin Routes */}
                <Route path="/admin/logs" element={<AdminLogsPage />}/>
                <Route path="/admin/stadt" element={<StadtListPage />} />
                <Route path="/admin/invitations" element={<InvitationsPage />} />
                <Route path="/admin/sessions" element={<SessionsPage />} />
                <Route path="/admin/settings" element={<SettingsPage />} />
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </Layout>
          </ProtectedRoute>
        }
      />
    </Routes>
    </ConfirmProvider>
    </DropdownProvider>
  );
}

export default App;
